<template>
  <div class="Activity">
    <div class="d-flex justify-end align-center mb-3">
      <strong>Server Time (UTC): {{ formatTime(new Date()) }} </strong>

      <v-btn icon color="green" @click="getInfo" class="ml-5">
        <v-icon>mdi-cached</v-icon>
      </v-btn>
    </div>

    <v-row class="Activity__filter-row d-flex flex-wrap">
      <v-col cols="3">
        <v-select @change="onActivityTypeChange" v-model="activityType" :items="activityTypes" outlined dense
          item-text="name" item-value="value" label="Workflow Status" />
      </v-col>

      <v-col cols="2">
        <v-text-field v-on:input="debounceInput" outlined dense v-model="search" name="Search" label="Search" />
      </v-col>

      <v-col cols="2" v-if="showRunType">
        <v-select @change="getInfo" v-model="runType" :items="triggerTypes" outlined dense item-text="name"
          item-value="value" label="Run Type" />
      </v-col>

      <v-col cols="2" v-if="showDateRange">
        <app-date-picker :range="true" v-model="dateRange" name="date" label="Time Interval" />
      </v-col>

      <v-col cols="1" v-if="showDaySelector">
        <day-of-week-picker v-model="dayOfWeek" name="date" label="Select Day of Week" />
      </v-col>
    </v-row>

    <div>
      <activity-workflow v-if="activityType === 'completed_workflows'" :reportType="activityType" :rows="activity"
        :loading="isDataLoading" :stats-loading="activityStatsLoading" :query-data="{ dateRange, localTZ, search }"
        @get-stat="onGetStat" :runType="runType" />
      <running-workflows v-else-if="isShAdmin && activityType === 'running_workflows'" :reportType="activityType"
        :rows="runningWorkflows" :loading="isDataLoading" />
      <failed-workflows v-else-if="isShAdmin && activityType === 'failed_workflows'" :reportType="activityType"
        :rows="failedWorkflows" :loading="isDataLoading" />
      <activity-transaction v-else-if="activityType === 'patient_transactions'" :reportType="activityType"
        :rows="activity" :loading="isDataLoading" />
      <upcoming-workflow v-else-if="activityType === 'scheduled_workflows'" :reportType="activityType"
        :rows="scheduledWorkflows" :query-data="{ dayOfWeek, search }" :loading="isDataLoading" />
    </div>
  </div>
</template>

<script>
import * as moment from 'moment-timezone';
import { createNamespacedHelpers } from 'vuex';
import ActivityWorkflow from '@/components/activity-workflow/activity-workflow.vue';
import UpcomingWorkflow from '@/components/activity-workflow/upcoming-workflow.vue';
import RunningWorkflows from '@/components/activity-workflow/running-workflows.vue';
import FailedWorkflows from '@/components/activity-workflow/failed-workflows.vue';
import ActivityTransaction from '@/components/activity-transaction/activity-transaction.vue';
import AppDatePicker from '@/components/app-date-picker/app-date-picker.vue';
import DayOfWeekPicker from '@/components/day-of-week-picker/day-of-week-picker.vue';
import debounce from 'lodash/debounce';

const { mapGetters: customerGetters } = createNamespacedHelpers('customer');
const {
  mapGetters: activityGetters,
  mapActions: activityActions,
  mapMutations: activityMutations,
} = createNamespacedHelpers('activity');

export default {
  name: 'Activity',
  components: {
    ActivityWorkflow,
    ActivityTransaction,
    AppDatePicker,
    DayOfWeekPicker,
    RunningWorkflows,
    FailedWorkflows,
    UpcomingWorkflow,
  },
  data() {
    return {
      dateRange: [new Date(), new Date()],
      search: '',
      localTZ: 'UTC',
      activityType: 'completed_workflows',
      activityTypes: [
        { name: 'Completed', value: 'completed_workflows' },
        { name: 'Scheduled', value: 'scheduled_workflows' },
        // { name: 'Patient Transactions', value: 'patient_transactions' },
      ],
      triggerTypes: [
        { name: 'All', value: 'all' },
        { name: 'Scheduled Job', value: 'cron' },
        { name: 'Webhook', value: 'event' },
        { name: 'Triggered', value: 'run_workflow_connector' },
        { name: 'AI General Action', value: 'ai_general_action' },
        { name: 'AI Custom Action', value: 'ai_custom_action' },
        { name: 'Manual', value: 'manual' },
      ],
      runType: 'cron',
      dayOfWeek: null,
    };
  },
  computed: {
    ...customerGetters({
      customer: 'CUSTOMER',
      selectedCustomer: 'SELECTED_CUSTOMER',
    }),
    ...activityGetters({
      activity: 'ACTIVITY',
      runningWorkflows: 'RUNNING_WORKFLOWS',
      failedWorkflows: 'FAILED_WORKFLOWS',
      activityLoading: 'ACTIVITY_LOADING',
      activityStatsLoading: 'ACTIVITY_STAT_LOADING',
      activityPage: 'GET_ACTIVITY_PAGE',
      activityLimit: 'GET_ACTIVITY_LIMIT',
      scheduledWorkflows: 'SCHEDULED_WORKFLOWS',
    }),
    isShAdmin() {
      return this.customer && this.customer.customer_id === 0;
    },
    isDataLoading() {
      return this.activityLoading;
    },
    showDateRange() {
      return (
        this.activityType === 'completed_workflows' ||
        this.activityType === 'failed_workflows'
      );
    },
    showDaySelector() {
      return this.activityType === 'scheduled_workflows';
    },
    showRunType() {
      return (
        this.activityType === 'completed_workflows' ||
        this.activityType === 'failed_workflows'
      );
    },
  },
  watch: {
    dateRange() {
      this.getInfo();
    },
    dayOfWeek() {
      this.getInfo();
    },
    'selectedCustomer.customer_id'() {
      this.getInfo();
    },
  },
  methods: {
    ...activityActions(['getActivity', 'getStat']),
    ...activityMutations({
      setActivityPage: 'SET_ACTIVITY_PAGE',
    }),
    formatDate(date) {
      if (!date) return '';
      else return moment(date).tz(this.localTZ).format('YYYY-MM-DD HH:mm');
    },
    formatTime(date) {
      if (!date) return '';
      else return moment(date).tz('UTC').format('HH:mm');
    },
    getInfo() {
      const from = moment(this.dateRange[0]).format('YYYY-MM-DD');
      const to = moment(this.dateRange[1]).format('YYYY-MM-DD');

      if (!from || !to) return;

      this.getActivity({
        from: from,
        to: to,
        tz: this.localTZ,
        report_type: this.activityType,
        page: this.activityPage - 1,
        limit: this.activityLimit,
        search: this.search,
        runType: this.runType,
        dayOfWeek: this.dayOfWeek ? new Date(this.dayOfWeek).getDay() : null,
      });
    },
    onGetStat(id) {
      this.getStat({ storageId: id });
    },
    setLocalTimezone() {
      this.localTZ = moment.tz.guess();
    },
    onActivityTypeChange() {
      this.setActivityPage(1);
      this.getInfo();
    },
    debounceInput: debounce(function (e) {
      this.search = e;
      this.getInfo();
    }, 1000),
  },
  mounted() {
    // not sure why pagination is tracked in the global store. Will resolve later
    this.setActivityPage(1);
    this.setLocalTimezone();
    this.getInfo();

    if (this.isShAdmin) {
      this.activityTypes.push(
        ...[
          { name: 'Running', value: 'running_workflows' },
          { name: 'Failed Workflows', value: 'failed_workflows' },
        ]
      );
    }
  },
};
</script>

<style lang="scss" scoped>
@import './activity';
</style>
